import React, { createContext, useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import './Admin.css';
import Dashboard from './Dashboard';
import Doctors from './Doctors';
import Services from './Services';
import Blogs from './Blogs';
import Careers from './Careers';
import Leads from './Leads';
import Settings from './Settings';
import Users from './Users';
import Logs from './Logs';
import Loader from '../Components/Loader/Loader';

// ---------------------------------------------------------------
// API helper (same base URL rules as the public app)
// The backend is deployed separately (backend.amaanativf.com); admin calls
// are cross-origin, so the session cookie requires the backend's CORS to
// allow this origin with credentials.
// ---------------------------------------------------------------
const isLocalhost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const ADMIN_API =
  process.env.REACT_APP_API_BASE ||
  (isLocalhost ? 'http://localhost:3001/api' : 'https://backend.amaanativf.com/api');

/** Server origin that serves /uploads files (no /api suffix). */
export const FILES_BASE = ADMIN_API.replace(/\/api$/, '');

// The logged-in screen registers a handler here so an expired/invalid session
// returns the admin to the login form instead of failing every panel with 401.
let onUnauthorized = null;
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

export async function api(path, options = {}) {
  // `skipAuthHandler` keeps a background call (the /me bootstrap, or the login
  // request itself) from kicking the user back to the login form.
  const { skipAuthHandler, ...fetchOptions } = options;
  const res = await fetch(`${ADMIN_API}${path}`, {
    credentials: 'include',
    headers: fetchOptions.body instanceof FormData ? undefined : { 'Content-Type': 'application/json' },
    ...fetchOptions,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    data = null;
  }
  if (res.status === 401) {
    const err = new Error('Unauthorized');
    err.status = 401;
    if (onUnauthorized && !skipAuthHandler) onUnauthorized();
    throw err;
  }
  if (!res.ok) {
    throw new Error((data && data.message) || `Request failed (${res.status})`);
  }
  return data;
}

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function Login() {
  const { setUser, setAuthed } = useAuth();
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const d = await api('/login', {
        method: 'POST',
        skipAuthHandler: true, // a rejected login is not an expired session
        body: JSON.stringify({ username, password }),
      });
      setUser(d.user || null);
      setAuthed(true);
    } catch (err) {
      setError(err.message === 'Unauthorized' ? 'Invalid username or password' : err.message);
    }
    setLoading(false);
  };

  return (
    <div className="admin-login-wrap">
      <form className="admin-login-card" onSubmit={submit}>
        <h1>Amaanat IVF</h1>
        <p className="admin-login-sub">Admin Panel</p>
        {error && <div className="admin-alert admin-alert-error">{error}</div>}
        <label>
          Username
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button className="admin-btn admin-btn-primary" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}

// Routes visible only to full admins.
const ADMIN_ONLY_LINKS = ['/admin/users', '/admin/logs', '/admin/settings'];

function Layout({ children }) {
  const { logout, user } = useAuth();
  const isAdmin = (user && user.role) === 'admin';
  const links = [
    ['/admin', 'Dashboard'],
    ['/admin/doctors', 'Doctors'],
    ['/admin/services', 'Services'],
    ['/admin/blogs', 'Blogs'],
    ['/admin/careers', 'Careers'],
    ['/admin/leads', 'Leads'],
    ['/admin/users', 'Users'],
    ['/admin/logs', 'Logs'],
    ['/admin/settings', 'Settings'],
  ].filter(([to]) => isAdmin || !ADMIN_ONLY_LINKS.includes(to));
  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">Amaanat IVF</div>
        <nav>
          {links.map(([to, label]) => (
            <Link key={to} to={to} className="admin-navlink">
              {label}
            </Link>
          ))}
        </nav>
        <button className="admin-navlink admin-logout" onClick={logout}>
          Log out
        </button>
      </aside>
      <main className="admin-main">{children}</main>
    </div>
  );
}

/** Guards the admin-only tabs. Non-admins get a clear notice, not the page. */
function AdminOnly({ children }) {
  const { user } = useAuth();
  if ((user && user.role) !== 'admin') {
    return (
      <div className="admin-alert admin-alert-error" style={{ marginTop: 20 }}>
        You need full admin rights to open this section. Ask an administrator for access.
      </div>
    );
  }
  return children;
}

function AdminRoutes() {
  const { authed } = useAuth();
  if (!authed) {
    return <Login />;
  }
  // Mounted at /admin/* inside App.js, so route paths here are relative
  // to /admin (e.g. "/doctors" -> /admin/doctors).
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/doctors" element={<Doctors />} />
        <Route path="/services" element={<Services />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/leads" element={<Leads />} />
        <Route
          path="/users"
          element={
            <AdminOnly>
              <Users />
            </AdminOnly>
          }
        />
        <Route
          path="/logs"
          element={
            <AdminOnly>
              <Logs />
            </AdminOnly>
          }
        />
        <Route
          path="/settings"
          element={
            <AdminOnly>
              <Settings />
            </AdminOnly>
          }
        />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Layout>
  );
}

export default function AdminApp() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [user, setUser] = useState(null);

  // Any 401 from the API (expired session, restarted server) sends the user
  // back to the login form with a clear message.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setAuthed(false);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let mounted = true;
    // /me returns 401 when logged out and the current role when logged in.
    // It must not use the global 401 handler: this check runs on every page
    // load, and a stale one resolving late would evict a just-completed login.
    api('/me', { skipAuthHandler: true })
      .then((d) => {
        if (!mounted) return;
        setUser(d.user || null);
        setAuthed(true);
      })
      .catch(() => mounted && setAuthed(false));
    return () => {
      mounted = false;
    };
  }, []);

  const logout = async () => {
    try {
      await api('/logout');
    } catch (_) {
      /* ignore */
    }
    setUser(null);
    setAuthed(false);
  };

  if (authed === null) {
    return <Loader status="Signing you in" />;
  }

  return (
    <AuthCtx.Provider value={{ authed, setAuthed, user, setUser, logout }}>
      {/* No BrowserRouter here: the public App.js already provides the router,
          and this component is mounted at /admin/* inside it. */}
      <AdminRoutes />
    </AuthCtx.Provider>
  );
}
